// DLG-010, legacy layouts/app/AppHeader.vue `.ui-reseller-notification`: a Radish admin or reseller logged into a
// location sees "LOGGED IN AS ADMIN" / "LOGGED IN AS RESELLER" above the whole page, with Logout on the left. Logout
// leaves the location (the account stays open) and goes to Accounts, as legacy's goBackToAccount does.
import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, LogOut } from 'lucide-react';
import { useToast } from '@radish/ui-web';
import { useActiveLocation } from '../app/location.tsx';
import { switchTo } from '../session/session.ts';

export function ImpersonationBar(): ReactNode {
  const location = useActiveLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { current } = location.session.view;
  if (current.via === 'membership') return null;
  // Accounts first: while the location's layout is mounted it would switch straight back into the location.
  const logout = async () => {
    setBusy(true);
    await navigate({ to: '/accounts' });
    const result = await switchTo(current.accountId, null);
    if (!result.ok) toast.error(result.message);
  };
  return (
    <div
      role="region"
      aria-label="Logged in as"
      data-testid="impersonation-bar"
      className="flex h-8 shrink-0 items-center justify-between bg-[var(--admin-topbar-background)] px-3 text-[var(--admin-topbar-ink)]"
    >
      <div className="flex-1">
        <button
          type="button"
          onClick={() => void logout()}
          disabled={busy}
          className="inline-flex h-7 items-center gap-1.5 rounded-q px-4 text-[11px] tracking-[0.25px] uppercase hover:bg-black/5 disabled:opacity-60"
        >
          <ArrowLeft size={19} aria-hidden className="hidden min-[600px]:inline" />
          <LogOut size={19} aria-hidden className="min-[600px]:hidden" />
          Logout
        </button>
      </div>
      <div
        data-testid="impersonation-message"
        className="flex-1 text-center text-[12px] tracking-[0.25px] whitespace-nowrap min-[600px]:text-[18px] min-[1024px]:text-[14px] min-[1024px]:font-medium"
      >
        {current.via === 'radish_staff' ? 'LOGGED IN AS ADMIN' : 'LOGGED IN AS RESELLER'}
      </div>
      {/* legacy @media 421: AppHeader.vue's (max-width: 420px) hides the spacer. */}
      <div className="hidden flex-1 min-[421px]:block" aria-hidden />
    </div>
  );
}
